import { Common, parseDate } from '@icc/shared/helpers';
import { core } from '@icc/shared/helpers/b2b';
import { DocumentSummary } from '../models/document-summary.model';
import { groupingSchemas } from '../models/grouping-schemas';
import { SalesDocumentPosition } from '../models/sales-document-position.model';
import { SalesDocument } from '../models/sales-document.model';
import { TaxRatesService } from './tax-rates.service';

export class DocumentSummarize {
    static summarize(
        salesDocument: SalesDocument,
        positions: SalesDocumentPosition[],
        purchasedMode?: boolean,
        buyPositionsMap?: Map<string, SalesDocumentPosition>,
        toNet?: boolean
    ): DocumentSummary {
        if (!positions) {
            positions = [];
        }

        const summary: DocumentSummary = {
            currency: salesDocument.priceCurrency,
            positions: null,
            groupsPositions: [],
            groupings: [],
            totalBeforeDiscountsAndPromotions: null,
            promotion: {
                promotion: 0,
                value: 0
            },
            totalAfterPromotions: null,
            discount: {
                discount: 0,
                value: 0
            },
            totalAfterPositionsDiscounts: null,
            additionalDiscount: {
                discount: salesDocument.priceAdditionalDiscount,
                value: 0
            },
            totalDiscount: 0,
            totalAfterDiscounts: null,
            taxes: [],
            totalTax: 0,
            totalGrossAfterDiscounts: null,
            validityDate: null,
            positionsCount: 0,
            positionsQuantity: 0,
            isInvalid: false,
            partialExternalSource: false,
            hasNonValuatedPositions: false,
            hasOnlyCustomValuatedPositions: true,
            nonValuatedPositionsCount: 0,
        };

        summary.totalBeforeDiscountsAndPromotions = 0;
        summary.totalAfterPromotions = 0;
        summary.totalAfterPositionsDiscounts = 0;
        summary.totalAfterDiscounts = 0;

        salesDocument.groups.forEach((grouping) => {
            const groupPositions = {
                groupSchemaId: grouping.groupSchemaId,
                name: groupingSchemas[grouping.groupSchemaId].name,
                total: 0,
            };
            positions
                .filter((pos) => grouping.groups.map((g) => g.groupPositionId).includes(pos.uuid))
                .forEach((pos) => {
                    const priceBeforeDiscountsValue = toNet
                        ? TaxRatesService.recalcPrice(
                              !toNet,
                              pos.priceBeforePromotionsValue,
                              pos.priceIsGross,
                              pos.priceTaxRate
                          )
                        : pos.priceBeforePromotionsValue;
                    groupPositions.total += core.round(
                        priceBeforeDiscountsValue * Number(pos.quantity || 0)
                    );
                });
            if (groupPositions.total !== 0) {
                summary.groupsPositions.push(groupPositions);
            }

            const groupingRow = {
                groupSchemaId: grouping.groupSchemaId,
                groups: [],
            };

            grouping.groups.forEach((gr) => {
                const group = {
                    key: gr.key,
                    name: gr.name,
                    value: 0,
                };
                positions
                    .filter((pos) => gr.positionIds.includes(pos.uuid))
                    .forEach((pos) => {
                        const priceBeforeDiscountsValue = toNet
                            ? TaxRatesService.recalcPrice(
                                  !toNet,
                                  pos.priceBeforePromotionsValue ?? pos.priceBeforeDiscountsValue,
                                  pos.priceIsGross,
                                  pos.priceTaxRate
                              )
                            : (pos.priceBeforePromotionsValue ?? pos.priceBeforeDiscountsValue);
                        group.value += core.round(
                            priceBeforeDiscountsValue * Number(pos.quantity || 0)
                        );
                    });
                if (group.value !== 0) {
                    groupingRow.groups.push(group);
                }
            });

            if (groupingRow.groups.length > 0) {
                summary.groupings.push(groupingRow);
            }
        });

        for (const pos of positions) {
            if (!pos.isDeleted && (!purchasedMode || buyPositionsMap?.has(pos.uuid))) {
                const priceBeforePromotionsValue = toNet
                    ? TaxRatesService.recalcPrice(
                          !toNet,
                          pos.priceBeforePromotionsValue ?? pos.priceBeforeDiscountsValue,
                          pos.priceIsGross,
                          pos.priceTaxRate
                      )
                    : (pos.priceBeforePromotionsValue ?? pos.priceBeforeDiscountsValue);
                const priceBeforeDiscountsValue = toNet
                    ? TaxRatesService.recalcPrice(
                          !toNet,
                          pos.priceBeforeDiscountsValue,
                          pos.priceIsGross,
                          pos.priceTaxRate
                      )
                    : pos.priceBeforeDiscountsValue;
                const priceAfterDiscountsValue = toNet
                    ? TaxRatesService.recalcPrice(
                          !toNet,
                          pos.priceAfterDiscountsValue,
                          pos.priceIsGross,
                          pos.priceTaxRate
                      )
                    : pos.priceAfterDiscountsValue;
                if (pos.type !== 'group') {
                    summary.positions += core.round(
                        priceBeforePromotionsValue * Number(pos.quantity || 0)
                    );
                }
                summary.totalBeforeDiscountsAndPromotions += core.round(
                    priceBeforePromotionsValue * Number(pos.quantity || 0)
                );

                if (Array.isArray(pos.pricePromotions)) {
                    for (const positionPromotion of pos.pricePromotions) {
                        const positionPromotionValue = toNet
                            ? TaxRatesService.recalcPrice(
                                  !toNet,
                                  positionPromotion.value,
                                  pos.priceIsGross,
                                  pos.priceTaxRate
                              )
                            : positionPromotion.value;
                        summary.promotion.value += positionPromotionValue * Number(pos.quantity || 0);
                    }
                }
                summary.promotion.value = core.round(summary.promotion.value);
                summary.promotion.promotion = core.round((summary.promotion.value / summary.totalBeforeDiscountsAndPromotions) * 100);
                summary.totalAfterPromotions += core.round(
                    priceBeforeDiscountsValue * Number(pos.quantity || 0)
                );

                if (Array.isArray(pos.priceDiscounts)) {
                    for (const positionDiscount of pos.priceDiscounts) {
                        const positionDiscountValue = toNet
                            ? TaxRatesService.recalcPrice(
                                    !toNet,
                                    positionDiscount.value,
                                    pos.priceIsGross,
                                    pos.priceTaxRate
                            )
                            : positionDiscount.value;
                        summary.discount.value += positionDiscountValue * (salesDocument.discountType === 'price' ? pos.quantity : 1);
                    }
                }
                summary.discount.discount = core.round((summary.discount.value / summary.totalAfterPromotions) * 100);
                let totalAfterDiscounts = core.round(
                    priceAfterDiscountsValue * (salesDocument.discountType === 'price' ? Number(pos.quantity || 0) : 1)
                );
                summary.totalAfterPositionsDiscounts += totalAfterDiscounts;
                let additionalDiscount = 0;
                if (pos.type !== 'additional' || !Common.isDefined(pos.configuration?.subjectToAdditionalDiscount) || pos.configuration?.subjectToAdditionalDiscount) {
                    additionalDiscount = core.round(totalAfterDiscounts * salesDocument.priceAdditionalDiscount / 100);
                    summary.additionalDiscount.value += additionalDiscount;
                }
                totalAfterDiscounts = totalAfterDiscounts - additionalDiscount;
                summary.totalAfterDiscounts += totalAfterDiscounts;

                summary.totalDiscount = summary.totalAfterPromotions - summary.totalAfterDiscounts;

                let tax = summary.taxes.find((el) => el.taxRateId === pos.priceTaxRateId);
                if (!tax) {
                    tax = {
                        taxRateId: pos.priceTaxRateId,
                        taxRate: pos.priceTaxRate,
                        value: 0,
                        tax: 0,
                    };
                    summary.taxes.push(tax);
                }
                tax.value += totalAfterDiscounts;

                if (salesDocument.taxRateCalculationMethod === 'netPosition') {
                    summary.totalTax += core.round((totalAfterDiscounts * pos.priceTaxRate) / 100);
                }

                const validityDate = pos.validityDate && parseDate(pos.validityDate);
                if (validityDate) {
                    if (!summary.validityDate || summary.validityDate > validityDate) {
                        summary.validityDate = validityDate;
                    }
                }
                summary.positionsCount = summary.positionsCount + 1;
                summary.positionsQuantity = summary.positionsQuantity + Number(pos.quantity || 0);
                summary.isInvalid = summary.isInvalid || pos.isInvalid;
                summary.partialExternalSource = summary.partialExternalSource || pos.externalSource === 'ERP';
                summary.hasNonValuatedPositions = summary.hasNonValuatedPositions ||
                    pos.priceBeforeDiscountsValue === null ||
                    Number.isNaN(pos.priceBeforeDiscountsValue) ||
                    typeof pos.priceBeforeDiscountsValue === 'undefined';
                summary.nonValuatedPositionsCount = summary.nonValuatedPositionsCount + ((pos.priceBeforeDiscountsValue === null || Number.isNaN(pos.priceBeforeDiscountsValue) || typeof pos.priceBeforeDiscountsValue === 'undefined') ? 1 : 0);
                summary.hasOnlyCustomValuatedPositions = summary.hasOnlyCustomValuatedPositions && pos.customPriceValue;
            }
        }

        if (Number.isNaN(summary.totalAfterDiscounts)) {
            summary.totalAfterDiscounts = null;
            summary.totalDiscount = null;
        }

        summary.taxes.forEach((tax) => {
            if (salesDocument.taxRateCalculationMethod === 'grossSummary' && !toNet) {
                tax.tax = core.round(
                    (tax.taxRate / 100) * TaxRatesService.recalcToNetPrice(tax.value, tax.taxRate)
                );
            } else {
                tax.tax = core.round((tax.taxRate / 100) * tax.value);
            }
            if (
                salesDocument.taxRateCalculationMethod === 'netSummary' ||
                salesDocument.taxRateCalculationMethod === 'grossSummary'
            ) {
                summary.totalTax += tax.tax;
            }
        });

        if (salesDocument.taxRateCalculationMethod === 'grossSummary' && !toNet) {
            summary.totalGrossAfterDiscounts = summary.totalAfterDiscounts;
        } else {
            summary.totalGrossAfterDiscounts = summary.totalAfterDiscounts + summary.totalTax;
        }

        return summary;
    }
}
